import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Clock, ArrowLeft, Printer, Tag, User, Calendar, ChevronRight, HelpCircle, List } from "lucide-react";
import { SiteLayout } from "@/components/site/SiteLayout";
import { CTASection } from "@/components/site/CTASection";
import { ArticleCard } from "@/components/blog/ArticleCard";
import { ArticleContent, extractTOCItems } from "@/components/blog/ArticleContent";
import { 
  getArticleBySlug, 
  getRelatedArticles, 
  categoryColors,
  toISO 
} from "@/data/blogContent";
import { getArticleFaq } from "@/data/articleFaqs";
import { SEOHead } from "@/components/SEOHead";
import { ArticleSchema, BreadcrumbSchema } from "@/components/SchemaOrg";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const ArticlePage = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const article = getArticleBySlug(slug || "");
  const faqData = slug ? getArticleFaq(slug) : undefined;

  if (!article) {
    return (
      <SiteLayout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <h1 className="font-heading text-3xl font-bold text-autosur-navy mb-4">
              Article non trouvé
            </h1>
            <p className="font-body text-muted-foreground mb-6">
              Cet article n'existe pas ou a été supprimé.
            </p>
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-autosur-blue font-heading font-semibold hover:text-autosur-navy transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              Retour au blog
            </Link>
          </div>
        </div>
      </SiteLayout>
    );
  }

  const relatedArticles = getRelatedArticles(article.slug, 3);
  const categoryClass = categoryColors[article.category] || "bg-gray-100 text-gray-800";
  const tocItems = extractTOCItems(article.content);
  const publishedISO = toISO(article.publishedAt);
  const modifiedISO = article.modifiedAt ? toISO(article.modifiedAt) : publishedISO;

  const handlePrint = () => {
    window.print();
  };

  return (
    <SiteLayout>
      <SEOHead
        title={article.title}
        description={article.excerpt}
        path={`/blog/${article.slug}`}
        type="article"
        image={article.image}
        author={article.author}
        publishedTime={publishedISO}
      />
      <ArticleSchema
        title={article.title}
        description={article.excerpt}
        slug={article.slug}
        publishedAt={publishedISO}
        modifiedAt={modifiedISO}
        author={article.author}
        image={article.image}
        faq={faqData}
      />
      <BreadcrumbSchema items={[
        { name: "Accueil", href: "/" },
        { name: "Blog", href: "/blog" },
        { name: article.title, href: `/blog/${article.slug}` }
      ]} />

      {/* Hero with image */}
      <section className="relative h-[40vh] md:h-[50vh] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={article.image}
            alt={article.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-autosur-navy via-autosur-navy/60 to-transparent" />
        </div>

        <div className="container mx-auto px-6 relative z-10 h-full flex items-end pb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <div className="flex items-center gap-3 mb-4">
              <span className={`px-3 py-1 rounded-full text-sm font-heading font-semibold ${categoryClass}`}>
                {article.category}
              </span>
            </div>

            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
              {article.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-white/70 text-sm font-body">
              <span className="flex items-center gap-2">
                <User className="w-4 h-4" />
                {article.author}
              </span>
              <span className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <time dateTime={publishedISO}>{article.publishedAt}</time>
              </span>
              <span className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                {article.readTime} de lecture
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Breadcrumb + Back + Print */}
      <div className="bg-gray-50 border-b">
        <div className="container mx-auto px-6 py-4">
          {/* Visual Breadcrumb */}
          <nav aria-label="Fil d'Ariane" className="mb-3 print:hidden">
            <ol className="flex items-center gap-1.5 text-sm text-muted-foreground font-body flex-wrap">
              <li>
                <Link to="/" className="hover:text-autosur-blue transition-colors">Accueil</Link>
              </li>
              <li><ChevronRight className="w-3.5 h-3.5" /></li>
              <li>
                <Link to="/blog" className="hover:text-autosur-blue transition-colors">Blog</Link>
              </li>
              <li><ChevronRight className="w-3.5 h-3.5" /></li>
              <li className="text-autosur-navy font-medium line-clamp-1">{article.title}</li>
            </ol>
          </nav>
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 text-autosur-navy hover:text-autosur-blue font-heading font-semibold transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              Retour
            </button>
            <button
              onClick={handlePrint}
              className="inline-flex items-center gap-2 text-muted-foreground hover:text-autosur-navy font-body transition-colors print:hidden"
            >
              <Printer className="w-5 h-5" />
              Imprimer
            </button>
          </div>
        </div>
      </div>

      {/* Article content */}
      <article className="py-12 bg-background print:py-4">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto">
            {/* Excerpt */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="font-body text-lg text-muted-foreground mb-8 border-l-4 border-autosur-red pl-4"
            >
              {article.excerpt}
            </motion.p>

            {/* Table of Contents */}
            {tocItems.length > 2 && (
              <motion.nav
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.25 }}
                aria-label="Sommaire de l'article"
                className="mb-10 p-5 bg-gray-50 rounded-2xl border border-gray-200 print:hidden"
              >
                <h2 className="font-heading font-bold text-autosur-navy mb-3 flex items-center gap-2 text-base">
                  <List className="w-4 h-4 text-autosur-blue" />
                  Sommaire
                </h2>
                <ol className="space-y-1.5">
                  {tocItems.map((item, i) => (
                    <li key={i}>
                      <a
                        href={`#${item.id}`}
                        className="flex items-center gap-2 text-sm font-body text-autosur-blue hover:text-autosur-navy hover:underline transition-colors"
                        onClick={(e) => {
                          e.preventDefault();
                          document.getElementById(item.id)?.scrollIntoView({ behavior: 'smooth' });
                        }}
                      >
                        <ChevronRight className="w-3 h-3 flex-shrink-0" />
                        {item.title}
                      </a>
                    </li>
                  ))}
                </ol>
              </motion.nav>
            )}

            {/* Content */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <ArticleContent content={article.content} />
            </motion.div>

            {/* FAQ Section */}
            {faqData && faqData.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.32 }}
                className="mt-12 print:hidden"
              >
                <div className="flex items-center gap-3 mb-6">
                  <HelpCircle className="w-6 h-6 text-autosur-blue" />
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-autosur-navy">
                    Questions fréquentes
                  </h2>
                </div>
                <Accordion type="single" collapsible className="space-y-3">
                  {faqData.map((item, index) => (
                    <AccordionItem
                      key={index}
                      value={`faq-${index}`}
                      className="border border-gray-200 rounded-xl px-5 data-[state=open]:border-autosur-blue/30 data-[state=open]:bg-autosur-blue/5 transition-colors"
                    >
                      <AccordionTrigger className="font-heading font-semibold text-autosur-navy text-left hover:no-underline py-4">
                        {item.question}
                      </AccordionTrigger>
                      <AccordionContent className="font-body text-muted-foreground pb-4">
                        {item.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </motion.div>
            )}

            {/* CTA inline article */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.35 }}
              className="mt-10 p-6 bg-gradient-to-r from-autosur-navy to-autosur-blue rounded-2xl text-center print:hidden"
            >
              <h3 className="font-heading text-xl font-bold text-white mb-2">
                Besoin d'un contrôle technique ?
              </h3>
              <p className="font-body text-white/70 text-sm mb-4">
                RDV en ligne 24/7 • Résultat immédiat • Tarifs dès 73€
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-autosur-red text-white font-heading font-semibold rounded-full hover:bg-autosur-red-light transition-colors shadow-md text-sm"
                >
                  Prendre RDV
                </Link>
                <a
                  href="tel:0149889240"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white/10 text-white font-heading font-semibold rounded-full hover:bg-white/20 transition-colors border border-white/30 text-sm"
                >
                  📞 01 49 88 92 40
                </a>
                <Link
                  to="/prestations"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white/10 text-white font-heading font-semibold rounded-full hover:bg-white/20 transition-colors border border-white/30 text-sm"
                >
                  Voir nos tarifs
                </Link>
              </div>
            </motion.div>

            {/* Internal linking block */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.37 }}
              className="mt-8 p-5 bg-gray-50 rounded-xl border border-gray-200 print:hidden"
            >
              <p className="font-heading font-semibold text-autosur-navy mb-3">À lire aussi sur notre blog :</p>
              <div className="grid sm:grid-cols-2 gap-2">
                <Link to="/vehicules/voitures" className="flex items-center gap-2 text-sm font-body text-autosur-blue hover:underline">
                  <ChevronRight className="w-3.5 h-3.5" /> CT voiture : tarifs et infos
                </Link>
                <Link to="/vehicules/motos" className="flex items-center gap-2 text-sm font-body text-autosur-blue hover:underline">
                  <ChevronRight className="w-3.5 h-3.5" /> CT moto : guide complet
                </Link>
                <Link to="/prestations" className="flex items-center gap-2 text-sm font-body text-autosur-blue hover:underline">
                  <ChevronRight className="w-3.5 h-3.5" /> Nos prestations et tarifs
                </Link>
                <Link to="/le-centre" className="flex items-center gap-2 text-sm font-body text-autosur-blue hover:underline">
                  <ChevronRight className="w-3.5 h-3.5" /> Notre centre à Montreuil
                </Link>
              </div>
            </motion.div>

            {/* Tags */}
            {article.tags.length > 0 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="mt-12 pt-8 border-t print:hidden"
              >
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="font-heading font-semibold text-autosur-navy flex items-center gap-2">
                    <Tag className="w-4 h-4" />
                    Explorer :
                  </span>
                  {article.tags.map((tag) => (
                    <Link
                      key={tag}
                      to={`/blog/tag/${tag}`}
                      className="px-3 py-1 bg-gray-100 hover:bg-autosur-blue/10 text-autosur-navy hover:text-autosur-blue rounded-full text-sm font-body transition-colors"
                    >
                      #{tag}
                    </Link>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Author card */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="mt-12 p-6 bg-gray-50 rounded-2xl print:hidden"
            >
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-autosur-blue/10 rounded-full flex items-center justify-center">
                  <User className="w-8 h-8 text-autosur-blue" />
                </div>
                <div>
                  <h4 className="font-heading font-bold text-autosur-navy">{article.author}</h4>
                  <p className="font-body text-muted-foreground text-sm">
                    Centre de contrôle technique Autosur Montreuil-sous-Bois • 343 avis ⭐ 4.9/5
                  </p>
                </div>
              </div>
              <p className="font-body text-muted-foreground text-sm mt-4">
                Les informations contenues dans cet article sont fournies à titre indicatif et ne sauraient 
                remplacer les conseils d'un professionnel ou la réglementation officielle en vigueur.
              </p>
            </motion.div>
          </div>
        </div>
      </article>

      {/* Related articles */}
      {relatedArticles.length > 0 && (
        <section className="py-12 bg-gray-50 print:hidden">
          <div className="container mx-auto px-6">
            <h2 className="font-heading text-2xl font-bold text-autosur-navy mb-8 text-center">
              À lire également
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {relatedArticles.map((related, index) => (
                <ArticleCard key={related.slug} article={related} index={index} />
              ))}
            </div>
          </div>
        </section>
      )}

      <CTASection 
        title="Une question sur cet article ?"
        subtitle="Notre équipe est à votre disposition pour répondre à toutes vos interrogations."
      />
    </SiteLayout>
  );
};

export default ArticlePage;
